'use client';

import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { Dropdown } from '@/shared/ui/dropdown';
import { FormFieldMessage } from './form-field-message';
import { FormFieldOptions, FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';

type Props = FormFieldProps<typeof Dropdown.Trigger> & {
    triggerContent: ReactNode;
    options: FormFieldOptions;
    isNullable?: boolean;
    contentProps?: ComponentPropsWithoutRef<typeof Dropdown.Content>;
};
export const FormDropdown = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    triggerContent,
    options,
    isNullable = false,
    ref,
    contentProps,
    ...props
}: Props) => (
    <Controller
        {...{ name, control, defaultValue, disabled, rules, shouldUnregister }}
        render={({
            field: { ref: fieldRef, onChange, value: selectedValue },
            fieldState: { error, isDirty, isTouched, invalid },
        }) => {
            const state = getFormFieldState({
                invalid,
                isDirty,
                isTouched,
                isErrorVisible,
                isSuccessVisible,
            });

            return (
                <>
                    <Dropdown>
                        <Dropdown.Trigger state={state} ref={mergeRefs([fieldRef, ref])} {...props}>
                            {triggerContent}
                        </Dropdown.Trigger>
                        <Dropdown.Content {...contentProps}>
                            {typeof options === 'function'
                                ? options(onChange)
                                : options.map(({ label, value }) => (
                                      <Dropdown.Item
                                          key={value}
                                          onClick={() => {
                                              if (isNullable && selectedValue === value) {
                                                  onChange('');
                                                  return;
                                              }
                                              onChange(value);
                                          }}
                                          selected={selectedValue === value}
                                      >
                                          {label}
                                      </Dropdown.Item>
                                  ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <FormFieldMessage state={state} error={error} messageWrapperProps={messageWrapperProps} />
                </>
            );
        }}
    />
);
